import {
  createContext,
  useContext,
  useMemo,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';

// A replacement for react-radio-group 3.0.3, which shared the group through the legacy context API that
// React 18.3 warns about and React 19 removes. Same markup: the group is a <div> with the remaining props,
// a radio is an <input type="radio"> with the group's name, checked when its value is the selected one.

export type TRadioValue = string | number | boolean;

interface IRadioGroupContext {
  name?: string;
  selectedValue?: TRadioValue | null;
  onChange?: (value: TRadioValue) => void;
}

export interface IRadioGroupProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'>, IRadioGroupContext {
  children: ReactNode;
}

export interface IRadioProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value'> {
  value?: TRadioValue;
}

const RadioGroupContext = createContext<IRadioGroupContext>({});

export const RadioGroup = ({
  name,
  selectedValue,
  onChange,
  children,
  ...rest
}: IRadioGroupProps) => {
  const group = useMemo(() => ({ name, selectedValue, onChange }), [name, selectedValue, onChange]);
  return (
    <RadioGroupContext.Provider value={group}>
      <div {...rest}>{children}</div>
    </RadioGroupContext.Provider>
  );
};

const toInputValue = (value: TRadioValue | undefined) => {
  if (value === undefined) return undefined;
  return String(value);
};

export const Radio = ({ value, ...rest }: IRadioProps) => {
  const { name, selectedValue, onChange } = useContext(RadioGroupContext);
  const checked = selectedValue === undefined ? undefined : value === selectedValue;
  const handleChange = () => {
    if (typeof onChange === 'function' && value !== undefined) onChange(value);
  };
  return (
    <input
      {...rest}
      value={toInputValue(value)}
      type="radio"
      name={name}
      checked={checked}
      onChange={typeof onChange === 'function' ? handleChange : undefined}
    />
  );
};
